import React, { useEffect, useRef, useState } from 'react';

import Icon from 'views/site/Icon.js';

import Request from 'files/Request.js';
import Section, { SectionHeader } from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Testimonials
 *
 * Real Google reviews for this dealership, from the same endpoint the full
 * testimonials page (views/site/Reviews.js) uses. The storefront's version of this section is hard
 * coded praise, which would be a fabricated testimonial attached to a real,
 * named local company — so none of it was carried over.
 *
 * If the dealership has no connected Google profile, or the fetch fails, or
 * there are no reviews yet, the section renders nothing at all. An empty
 * testimonials block is better than an invented one.
 */

const Testimonials = ({ dealership, utils }) => {

    const mobile = Utils.isMobile();
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);

    const enabled = dealership?.google_reviews?.enabled === true;

    // utils is rebuilt on every App render — pinning it keeps the fetch to one
    const utilsRef = useRef(utils);
    utilsRef.current = utils;

    useEffect(() => {

        if(enabled === false) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                let { reviews } = await Request.get(utilsRef.current, '/dealerships/', {
                    dealership_id: dealership.id,
                    type: 'google_business_profile_reviews'
                });
                if(cancelled === false) {
                    setReviews(reviews || []);
                    setLoading(false);
                }
            } catch(e) {
                console.log(e.message)
                // A failed review fetch is not worth an alert on a marketing
                // page — the section simply does not appear.
                if(cancelled === false) {
                    setLoading(false);
                }
            }
        })();

        return () => { cancelled = true; };

    }, [dealership?.id, enabled]);

    const getStars = rating => (
        <div style={{ display: 'flex', gap: 2 }}>
            {[...Array(5)].map((item, index) => (
                <Icon name={'star-fill'} size={12} color={index < (parseInt(rating) || 5) ? '#F5A623' : Theme.colors.lighterGrey} />
            ))}
        </div>
    );

    const getAverage = () => {
        let rated = reviews.filter(review => parseInt(review.rating) > 0);
        if(rated.length === 0) {
            return null;
        }
        let total = rated.reduce((sum, review) => sum + parseInt(review.rating), 0);
        return (total / rated.length).toFixed(1);
    };

    // Nothing to show — render nothing rather than an empty shell
    if(loading === true || reviews.length === 0) {
        return null;
    }

    let average = getAverage();

    return (
        <Section id={'testimonials'} tinted={true}>
            <SectionHeader
            eyebrow={'Reviews'}
            subtitle={average
                ? `Rated ${average} out of 5 stars across ${reviews.length} Google review${reviews.length === 1 ? '' : 's'} for ${dealership.name}. These are unedited and come straight from Google, so they are as easy for you to check as they are for us to show.`
                : `What families say about working with ${dealership.name}. These come from homes where the walkthrough happened first and the equipment came second, which is the order most people end up talking about it in.`}
            title={'In their own words'} />

            <div style={{
                display: 'grid',
                gap: mobile ? 16 : 20,
                gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)'
            }}>
                {reviews.slice(0, 6).map((review, index) => (
                    <div
                    key={index}
                    style={{
                        ...Theme.styles.card(),
                        boxShadow: Theme.boxShadow.card,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: mobile ? 24 : 26
                    }}>
                        {getStars(review.rating)}
                        <span style={{
                            ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15 }),
                            display: 'block',
                            flexGrow: 1,
                            margin: '14px 0 20px'
                        }}>{Utils.truncateText(review.text, 260)}</span>

                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 12
                        }}>
                            {review.avatar ? (
                                <img
                                alt={review.full_name}
                                src={review.avatar}
                                style={{
                                    borderRadius: 18,
                                    height: 36,
                                    objectFit: 'cover',
                                    width: 36
                                }} />
                            ) : (
                                <div style={{
                                    alignItems: 'center',
                                    backgroundColor: Theme.colors.offWhite,
                                    borderRadius: 18,
                                    display: 'flex',
                                    height: 36,
                                    justifyContent: 'center',
                                    width: 36
                                }}>
                                    <Icon name={'person-fill'} size={13} color={Theme.colors.lightGrey} />
                                </div>
                            )}
                            <div style={{ minWidth: 0 }}>
                                <span style={{
                                    ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 14, fontWeight: 600 }),
                                    display: 'block'
                                }}>{review.full_name}</span>
                                <span style={Theme.textStyles.caption()}>{review.date_relative || 'Google review'}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    )
}

export default Testimonials;
